import React, { useEffect, useState } from "react";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import { getCache, setCache, isFresh, invalidate } from "./adminCache";
import {
  AdminShell,
  Panel,
  Modal,
  ResultModal,
  StatCard,
  Chip,
  Toggle,
  inputCls,
  labelCls,
  primaryBtn,
  fmtDate,
  toDateInput,
  STATUS_OPTIONS,
  TASK_TYPES,
  Loading,
} from "./adminUi";

const statusLabel = (k) => STATUS_OPTIONS.find((s) => s.key === k)?.label || "Active";

// Build the small status chip shown on the result popup for a saved task.
const taskChip = (form) => {
  const parts = [statusLabel(form.status), `+${Number(form.points) || 0} pts`];
  if (form.deadline) parts.push(`Due ${fmtDate(form.deadline)}`);
  return parts.join(" · ");
};

const emptyForm = {
  header: "",
  description: "",
  deadline: "",
  points: "",
  status: "active",
  taskType: "Social Media",
  requireProof: true,
  checklistSteps: "",
};

const ManageTasks = () => {
  const [tasks, setTasks] = useState(() => getCache("tasks") || []);
  const [loading, setLoading] = useState(() => !getCache("tasks"));
  const [editing, setEditing] = useState(null); // task object or "new" or null
  const [result, setResult] = useState(null); // { kind, form, task } shown after a save/delete

  const fetchTasks = () => {
    adminApi
      .get("/tasks")
      .then((res) => { setTasks(res.data || []); setCache("tasks", res.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isFresh("tasks")) { setLoading(false); return; }
    fetchTasks();
  }, []);

  if (loading) return <Loading label="Loading tasks..." />;

  const activeTasks = tasks.filter((t) => (t.status || (t.isActive ? "active" : "closed")) === "active").length;
  const closedTasks = tasks.length - activeTasks;
  const totalPoints = tasks.reduce((s, t) => s + (t.points || 0), 0);

  return (
    <AdminShell
      tab={paths.adminTasks}
      title="Tasks"
      subtitle="Create, edit, and manage tasks for Campus Ambassadors."
      actions={
        <button onClick={() => setEditing("new")} className={primaryBtn}>+ Add Task</button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-2">
        <StatCard label="Total Tasks" value={tasks.length} sub={`${activeTasks} active · ${closedTasks} closed`} />
        <StatCard label="Active" value={activeTasks} sub="visible to CAs" />
        <StatCard label="Total Pts" value={totalPoints.toLocaleString()} sub="across all tasks" />
      </div>

      <Panel title="Task Management" sub={`${activeTasks} Active · ${closedTasks} Closed`}>
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-[#F5EFEB] z-10">
              <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                <th className="py-2 px-3">Task Title</th>
                <th className="py-2 px-3">Deadline</th>
                <th className="py-2 px-3 text-right">Pts</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {tasks.length === 0 && (
                <tr><td colSpan={5} className="text-center text-black/40 py-6">No tasks yet.</td></tr>
              )}
              {tasks.map((t) => {
                const st = t.status || (t.isActive ? "active" : "closed");
                const cls = st === "active" ? "bg-teal-100 text-teal-700" : st === "draft" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-500";
                return (
                  <tr key={t.id} className="border-b border-black/[0.03] last:border-none">
                    <td className="py-2.5 px-3 font-bold text-[#2B1B17] whitespace-nowrap">{t.header}</td>
                    <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">{fmtDate(t.deadline)}</td>
                    <td className="py-2.5 px-3 text-right font-bold text-green-600">+{t.points ?? 0}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md capitalize ${cls}`}>{st}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button onClick={() => setEditing(t)} className="text-xs font-bold text-[#D15B28] hover:opacity-80 whitespace-nowrap">Edit →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {editing && (
        <TaskModal
          task={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(res) => { setEditing(null); invalidate("dashboard"); fetchTasks(); setResult(res); }}
        />
      )}

      {result?.kind === "created" && (
        <ResultModal
          accent="teal"
          title="Task Published!"
          message={`${result.form.header} is now live and visible to all Campus Ambassadors.`}
          chip={taskChip(result.form)}
          primary={{ label: "View Task", onClick: () => setResult(null) }}
          secondary={{ label: "Create Another", onClick: () => { setResult(null); setEditing("new"); } }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "updated" && (
        <ResultModal
          accent="olive"
          title="Changes Saved!"
          message={`Your edits to ${result.form.header} have been applied immediately for all CAs.`}
          chip={taskChip(result.form)}
          primary={{ label: "View Task", onClick: () => setResult(null) }}
          secondary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "deleted" && (
        <ResultModal
          accent="red"
          danger
          title="Task Deleted"
          message={`${result.task.header} has been removed. CAs will no longer see it in their task list.`}
          primary={{ label: "Go to Task Manager", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}
    </AdminShell>
  );
};

const TaskModal = ({ task, onClose, onSaved }) => {
  const isEdit = !!task;
  const [form, setForm] = useState(
    isEdit
      ? {
          header: task.header || "",
          description: task.description || "",
          deadline: toDateInput(task.deadline),
          points: task.points ?? "",
          status: task.status || (task.isActive ? "active" : "closed"),
          taskType: task.taskType || "Social Media",
          requireProof: task.requireProof !== false,
          checklistSteps: task.checklistSteps || "",
        }
      : emptyForm
  );
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.header || !form.description) {
      alert("Task title and description are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        header: form.header,
        description: form.description,
        deadline: form.deadline || null,
        points: Number(form.points) || 0,
        status: form.status,
        taskType: form.taskType,
        requireProof: form.requireProof,
        checklistSteps: form.checklistSteps.trim() || null,
      };
      if (isEdit) await adminApi.put(`/tasks/${task.id}`, payload);
      else await adminApi.post("/tasks", payload);
      onSaved({ kind: isEdit ? "updated" : "created", form });
    } catch (err) {
      alert(err.response?.data?.message || "Could not save task");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${task.header}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.delete(`/tasks/${task.id}`);
      onSaved({ kind: "deleted", task });
    } catch (err) {
      alert(err.response?.data?.message || "Could not delete task");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal label={isEdit ? "Edit Task" : "Create Task"} title={isEdit ? task.header : "Add a new task for Campus Ambassadors"} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div>
          <label className={labelCls}>Task Title</label>
          <input value={form.header} onChange={(e) => set("header", e.target.value)} className={`${inputCls} mt-1.5`} placeholder="e.g. Instagram Reel Post" />
        </div>
        <div>
          <label className={labelCls}>Description</label>
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={`${inputCls} mt-1.5 resize-none`} placeholder="What should CAs do? Include platform, hashtags, requirements..." />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Deadline</label>
            <input type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className={labelCls}>Points Reward</label>
            <input type="number" value={form.points} onChange={(e) => set("points", e.target.value)} className={`${inputCls} mt-1.5`} placeholder="e.g. 50" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Task Type</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {TASK_TYPES.map((t) => (
              <Chip key={t} active={form.taskType === t} onClick={() => set("taskType", t)}>{t}</Chip>
            ))}
          </div>
          <p className="text-xs text-black/40 mt-1.5">
            “Registration” shows CAs a referral-progress modal. Add steps below to turn any task into a checklist.
          </p>
        </div>
        <div>
          <label className={labelCls}>
            Checklist Steps <span className="text-black/30 normal-case font-medium">(optional · one per line)</span>
          </label>
          <textarea
            value={form.checklistSteps}
            onChange={(e) => set("checklistSteps", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder={"Joined the Spirit '26 WhatsApp group\nShared the registration link to your status\nTagged 3 friends in the announcement post"}
          />
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {STATUS_OPTIONS.map((s) => (
              <Chip key={s.key} active={form.status === s.key} onClick={() => set("status", s.key)}>{s.label}</Chip>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-black/5 pt-4">
          <span className="font-semibold text-[#2B1B17]">Require proof submission</span>
          <Toggle on={form.requireProof} onClick={() => set("requireProof", !form.requireProof)} />
        </div>
        <div className="flex items-center gap-3 mt-1">
          <button onClick={save} disabled={busy} className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60">
            {busy ? "Saving..." : isEdit ? "Save Changes ➜" : "Create Task ➜"}
          </button>
          {isEdit && (
            <button onClick={remove} disabled={busy} className="px-6 py-3.5 rounded-full border border-red-300 text-red-500 font-semibold hover:bg-red-50 transition">
              Delete
            </button>
          )}
          <button onClick={onClose} className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17]">
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ManageTasks;
